import { z } from 'zod';

// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

// User Types
export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  createdAt: string;
  updatedAt: string;
}

export enum UserRole {
  DEALER = 'DEALER',
  DISTRIBUTOR = 'DISTRIBUTOR',
  ADMIN = 'ADMIN',
  REVIEWER = 'REVIEWER',
}

// Zod Schemas for validation
export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  name: z.string().min(1),
  role: z.nativeEnum(UserRole),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const CreateUserSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1),
  role: z.nativeEnum(UserRole).optional().default(UserRole.DEALER),
});

export type CreateUserInput = z.infer<typeof CreateUserSchema>;

// ----------------------------------------------------------------------
// Authentication & User Profile Types
// ----------------------------------------------------------------------

export enum UserStatus {
  UNKNOWN_USER = 'UNKNOWN_USER',
  INVITED = 'INVITED',
  KYC_PENDING = 'KYC_PENDING',
  KYC_UNDER_REVIEW = 'KYC_UNDER_REVIEW',
  KYC_INFO_REQUESTED = 'KYC_INFO_REQUESTED',
  KYC_REJECTED = 'KYC_REJECTED',
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

/**
 * A dealer's office. Every field is optional and every backing column is
 * nullable: the dealer fills this in over time (a pin now, an address later, a
 * photo when they next open the app), so a partial office has to round-trip.
 * Requiring all-or-nothing here is what used to make the onboarding form
 * silently discard everything the dealer had entered.
 */
export interface UserOffice {
  address?: string;
  area?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  location?: {
    lat: number;
    lng: number;
  } | null;
  photoURL?: string;
}

export interface UserHistory {
  lastLoginAt?: string;
  createdAt: string;
  approvedAt?: string;
  lastTransactedAt?: string;
}

export interface UserProfile {
  uid: string;
  email?: string;
  phoneNumber?: string;
  fullName: string;
  dob?: string;
  photoURL?: string;
  office?: UserOffice;
  history: UserHistory;
  status: UserStatus;
  role: UserRole;
  // Credit information (for dealers)
  availableCredit?: number;
  // Invited by information (for invited users)
  invitedBy?: {
    uid: string;
    role: string;
    fullName: string;
  };
  // KYC Documents
  kycDocuments?: KYCDocuments;
}

export interface KYCDocuments {
  aadhar?: {
    front?: string;
    back?: string;
    number?: string;
    uploadedAt?: string;
  };
  pan?: {
    document?: string;
    number?: string;
    uploadedAt?: string;
  };
  addressProof?: {
    document?: string;
    type?: string; // 'utility_bill' | 'bank_statement' | 'rental_agreement' | 'other'
    uploadedAt?: string;
  };
  profilePhoto?: {
    url?: string;
    uploadedAt?: string;
  };
  officePhoto?: {
    url?: string;
    uploadedAt?: string;
  };
  businessLicense?: {
    document?: string;
    number?: string;
    uploadedAt?: string;
  };
  gstCertificate?: {
    document?: string;
    number?: string;
    uploadedAt?: string;
  };
  bankDetails?: {
    accountNumber?: string;
    ifscCode?: string;
    bankName?: string;
    accountHolderName?: string;
  };
  additionalDocuments?: Array<{
    type: string;
    document: string;
    description?: string;
    uploadedAt?: string;
  }>;
}

// Auth Response Types
export interface AuthMeResponse {
  code: UserStatus;
  user?: UserProfile;
}

export interface AuthResponse<T = any> extends ApiResponse<T> {
  code?: UserStatus;
}

// Zod Schemas for validation
// Mirrors the UserOffice interface above: all-optional, because the columns are
// nullable and the dealer supplies these piecemeal.
//
// An empty string is meaningful here - it is how an update clears a column. An
// absent key leaves the column alone, so "" and undefined are NOT the same and
// must both be accepted. Formats are still enforced on any non-empty value.
const clearableText = z.string().optional();

export const UserOfficeSchema = z.object({
  address: clearableText,
  area: clearableText,
  city: clearableText,
  state: clearableText,
  country: clearableText,
  pincode: z
    .string()
    .refine((v) => v === '' || /^\d{6}$/.test(v), { message: 'Pincode must be 6 digits' })
    .optional(),
  location: z
    .object({
      lat: z.number().min(-90).max(90),
      lng: z.number().min(-180).max(180),
    })
    .nullable()
    .optional(),
  photoURL: z
    .string()
    .refine((v) => v === '' || z.string().url().safeParse(v).success, {
      message: 'Must be a valid URL',
    })
    .optional(),
});

export const UserHistorySchema = z.object({
  lastLoginAt: z.string().datetime().optional(),
  createdAt: z.string().datetime(),
  approvedAt: z.string().datetime().optional(),
  lastTransactedAt: z.string().datetime().optional(),
});

export const UserProfileSchema = z.object({
  uid: z.string().min(1),
  email: z.string().email().optional(),
  phoneNumber: z.string().optional(),
  fullName: z.string().min(1),
  dob: z.string().optional(), // Format: YYYY-MM-DD
  photoURL: z.string().url().optional(),
  office: UserOfficeSchema.optional(),
  history: UserHistorySchema,
  status: z.nativeEnum(UserStatus),
  role: z.nativeEnum(UserRole),
});

export const CreateUserProfileSchema = z.object({
  fullName: z.string().min(1, 'Full name is required'),
  dob: z.string().optional(),
  photoURL: z.string().url().optional(),
  office: UserOfficeSchema.optional(),
});

export const UpdateUserProfileSchema = CreateUserProfileSchema.partial();

export type CreateUserProfileInput = z.infer<typeof CreateUserProfileSchema>;
export type UpdateUserProfileInput = z.infer<typeof UpdateUserProfileSchema>;

// ----------------------------------------------------------------------
// Transaction Types (Eko DMT)
// ----------------------------------------------------------------------

// Transaction Types
export enum TransactionType {
  DMT = 'DMT',
  CASH_COLLECTION = 'CASH_COLLECTION',
  CASH_DEPOSIT = 'CASH_DEPOSIT',
}

export enum TransactionStatus {
  INITIATED = 'INITIATED',
  AGENT_ASSIGNED = 'AGENT_ASSIGNED',
  AGENT_PICKED_UP = 'AGENT_PICKED_UP',
  ADMIN_RECEIVED = 'ADMIN_RECEIVED',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  APPROVED = 'APPROVED',
  PROCESSING = 'PROCESSING',
  QUEUED = 'QUEUED',
  /** QUEUED for >30 min with no provider update — flagged for ops triage (08-b). */
  QUEUED_STUCK = 'QUEUED_STUCK',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  REVERSED = 'REVERSED',
}

export enum PaymentMode {
  IMPS = 'IMPS',
  NEFT = 'NEFT',
  RTGS = 'RTGS',
  UPI = 'UPI',
}

// Beneficiary Types (Contact level)
export enum BeneficiaryStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export interface Beneficiary {
  id: string;
  userId: string;
  name: string;
  phoneNumber: string;
  nickname?: string;
  photoURL?: string;
  isFavorite: boolean;
  status: BeneficiaryStatus;
  createdAt: string;
  updatedAt: string;
  /** Last 4 digits of the sender's Aadhaar — captured at sender-register, displayed masked. */
  aadhaarLast4?: string | null;
  /** Last 4 digits of the sender's PAN — optional, captured at sender-register. */
  panLast4?: string | null;
  /** Derived Eko KYC status (lockstep with ekoCustomerState). `FULL_KYC` means payout-ready. */
  kycStatus?: string | null;
  /** Eko customer state (4 = FULL_KYC). Populated for DMT senders. */
  ekoCustomerState?: number | null;
  metadata?: any;
  // Related payment methods (populated on fetch)
  paymentMethods?: PaymentMethod[];
}

// Payment Method Types
export enum PaymentMethodType {
  BANK_ACCOUNT = 'bank_account',
  VPA = 'vpa',
}

export enum PaymentMethodStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum ValidationStatus {
  PENDING = 'PENDING',
  VALIDATED = 'VALIDATED',
  FAILED = 'FAILED',
}

export interface PaymentMethod {
  id: string;
  beneficiaryId: string;
  userId: string;
  type: PaymentMethodType;
  accountHolderName: string;
  nickname?: string;
  // Bank account details
  accountNumber?: string;
  ifscCode?: string;
  bankName?: string;
  // UPI details
  vpaAddress?: string;
  // Provider integration
  providerContactId?: string;
  providerFundAccountId?: string;
  // Validation
  validationStatus: ValidationStatus;
  validationDetails?: any;
  validatedAt?: string;
  // Status
  isDefault: boolean;
  status: PaymentMethodStatus;
  // Timestamps
  createdAt: string;
  updatedAt: string;
  // Metadata
  metadata?: any;
}

// Transaction Types
export interface Transaction {
  id: string;
  transactionType: TransactionType;
  // Chain tracking
  dealerId: string;
  distributorId?: string;
  adminId?: string;
  beneficiaryId: string; // Legacy - kept for backwards compatibility
  paymentMethodId?: string; // New - references payment_methods table
  customerPhone?: string;
  // Financial details
  amount: number;
  currency: string;
  mode: PaymentMode;
  purpose: string;
  referenceId: string;
  notes?: any;
  // Status
  status: TransactionStatus;
  // Payout provider
  payoutProvider?: string;
  providerPayoutId?: string;
  providerStatus?: string;
  bankReference?: string;
  /**
   * Money breakdown as the payout provider reported it. `undefined` means the
   * provider did not report that component - render it as unavailable, never as
   * zero and never as a locally computed substitute.
   */
  providerFeeAmount?: number;
  commissionAmount?: number;
  taxAmount?: number;
  tdsAmount?: number;
  /** Total actually debited from float: amount + fee + tax - commission + tds. */
  totalDebitAmount?: number;
  /** Provider's specific reason text, more precise than its generic message. */
  providerReason?: string;
  // Credit
  creditApproved: boolean;
  creditCheckBypassed: boolean;
  creditReservedAmount?: number;
  // Timestamps
  initiatedAt: string;
  distributorAssignedAt?: string;
  distributorPickedUpAt?: string;
  adminReceivedAt?: string;
  approvedAt?: string;
  processingStartedAt?: string;
  completedAt?: string;
  failedAt?: string;
  // Error tracking
  failureReason?: string;
  failureCode?: string;
  // Metadata
  metadata?: any;
}

// Credit Line Types
export enum CreditLineStatus {
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  UNDER_REVIEW = 'UNDER_REVIEW',
}

export interface DealerCreditLine {
  id: string;
  dealerId: string;
  creditLimit: number;
  usedCredit: number;
  availableCredit: number;
  /**
   * @deprecated Commission moved to commission_hierarchy (m06). This field is
   * no longer populated by the backend (A1, 2026-04-26) but remains as
   * `?: number` so legacy FE consumers do not break at typecheck.
   */
  commissionPercentage?: number;
  status: CreditLineStatus;
  createdAt: string;
  updatedAt: string;
  metadata?: any;
}

// Credit Extension Request Types
export enum CreditExtensionStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export interface CreditExtensionRequest {
  id: string;
  dealerId: string;
  requestedBy: string;
  currentLimit: number;
  requestedLimit: number;
  reason?: string;
  status: CreditExtensionStatus;
  adminId?: string;
  adminNotes?: string;
  requestedAt: string;
  reviewedAt?: string;
  metadata?: any;
}

// Dealer Transaction Limit Types
export enum TransactionLimitStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

export interface DealerTransactionLimits {
  id: string;
  dealerId: string;
  perTransactionAmount: number;
  dailyTransactionCount: number;
  dailyTransactionAmount: number;
  weeklyTransactionCount: number;
  weeklyTransactionAmount: number;
  monthlyTransactionCount: number;
  monthlyTransactionAmount: number;
  /**
   * @deprecated Commission moved to commission_hierarchy (m06). No longer
   * populated by the backend (A1, 2026-04-26).
   */
  commissionPercentage?: number;
  status: TransactionLimitStatus;
  createdAt: string;
  updatedAt: string;
  metadata?: any;
}

export interface BeneficiaryTransactionUsage {
  beneficiaryPhone: string;
  daily: {
    count: number;
    amount: number;
    transactions: Transaction[];
  };
  weekly: {
    count: number;
    amount: number;
    transactions: Transaction[];
  };
  monthly: {
    count: number;
    amount: number;
    transactions: Transaction[];
  };
}

export interface TransactionLimitCheckResult {
  allowed: boolean;
  reason?: string;
  limits: DealerTransactionLimits;
  usage: BeneficiaryTransactionUsage;
  violations: {
    perTransaction?: boolean;
    dailyCount?: boolean;
    dailyAmount?: boolean;
    weeklyCount?: boolean;
    weeklyAmount?: boolean;
    monthlyCount?: boolean;
    monthlyAmount?: boolean;
  };
}

// Transaction Queue Types
export enum QueueStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export interface TransactionStatusQueue {
  id: string;
  transactionId: string;
  providerPayoutId: string;
  status: QueueStatus;
  retryCount: number;
  maxRetries: number;
  nextRetryAt: string;
  lastError?: string;
  lastCheckedAt?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  metadata?: any;
}

// Zod Schemas for Validation

// Beneficiary Schemas (Contact level)
export const CreateBeneficiarySchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  phoneNumber: z.string().min(10, 'Valid phone number is required'),
  nickname: z.string().optional(),
  photoURL: z.string().url().optional(),
  isFavorite: z.boolean().optional().default(false),
  aadhaarLast4: z
    .string()
    .regex(/^\d{4}$/, 'Aadhaar last-4 must be exactly 4 digits')
    .optional(),
  panLast4: z
    .string()
    .regex(/^[A-Z0-9]{4}$/, 'PAN last-4 must be 4 alphanumeric characters')
    .optional(),
  // First payment method (combined form)
  paymentMethod: z
    .object({
      type: z.nativeEnum(PaymentMethodType),
      accountHolderName: z.string().min(2, 'Account holder name must be at least 2 characters'),
      nickname: z.string().optional(),
      // Bank account fields
      accountNumber: z
        .string()
        .regex(/^\d{9,18}$/, 'Account number must be 9-18 digits')
        .optional(),
      ifscCode: z
        .string()
        .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC code format')
        .toUpperCase()
        .optional(),
      bankName: z.string().min(1).optional(),
      // UPI fields
      vpaAddress: z
        .string()
        .regex(/^[\w.-]+@[\w.-]+$/, 'Invalid UPI ID format')
        .optional(),
    })
    .refine(
      (data) => {
        if (data.type === PaymentMethodType.BANK_ACCOUNT) {
          return !!(data.accountNumber && data.ifscCode && data.bankName);
        }
        if (data.type === PaymentMethodType.VPA) {
          return !!data.vpaAddress;
        }
        return false;
      },
      {
        message:
          'Bank accounts must have accountNumber, ifscCode, and bankName. VPA must have vpaAddress.',
      }
    ),
});

export const UpdateBeneficiarySchema = z.object({
  name: z.string().min(2).optional(),
  nickname: z.string().optional(),
  photoURL: z.string().url().optional(),
  isFavorite: z.boolean().optional(),
  status: z.nativeEnum(BeneficiaryStatus).optional(),
});

export type CreateBeneficiaryInput = z.infer<typeof CreateBeneficiarySchema>;
export type UpdateBeneficiaryInput = z.infer<typeof UpdateBeneficiarySchema>;

// Payment Method Schemas
export const CreatePaymentMethodSchema = z
  .object({
    beneficiaryId: z.string().min(1, 'Beneficiary ID is required'),
    type: z.nativeEnum(PaymentMethodType),
    accountHolderName: z.string().min(2, 'Account holder name must be at least 2 characters'),
    nickname: z.string().optional(),
    // Bank account fields
    accountNumber: z
      .string()
      .regex(/^\d{9,18}$/, 'Account number must be 9-18 digits')
      .optional(),
    ifscCode: z
      .string()
      .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC code format')
      .toUpperCase()
      .optional(),
    bankName: z.string().min(1).optional(),
    // UPI fields
    vpaAddress: z
      .string()
      .regex(/^[\w.-]+@[\w.-]+$/, 'Invalid UPI ID format')
      .optional(),
  })
  .refine(
    (data) => {
      if (data.type === PaymentMethodType.BANK_ACCOUNT) {
        return !!(data.accountNumber && data.ifscCode && data.bankName);
      }
      if (data.type === PaymentMethodType.VPA) {
        return !!data.vpaAddress;
      }
      return false;
    },
    {
      message:
        'Bank accounts must have accountNumber, ifscCode, and bankName. VPA must have vpaAddress.',
    }
  );

export const UpdatePaymentMethodSchema = z.object({
  nickname: z.string().optional(),
  isDefault: z.boolean().optional(),
  status: z.nativeEnum(PaymentMethodStatus).optional(),
});

export type CreatePaymentMethodInput = z.infer<typeof CreatePaymentMethodSchema>;
export type UpdatePaymentMethodInput = z.infer<typeof UpdatePaymentMethodSchema>;

// Replace a payment method's account details (edit → re-verify). Provider fund
// accounts are immutable, so a replace provisions a NEW verified account and
// retires the old one. Same field rules as create, minus beneficiaryId (derived
// from the account being replaced).
export const ReplacePaymentMethodSchema = z
  .object({
    type: z.nativeEnum(PaymentMethodType),
    accountHolderName: z.string().min(2, 'Account holder name must be at least 2 characters'),
    nickname: z.string().optional(),
    accountNumber: z
      .string()
      .regex(/^\d{9,18}$/, 'Account number must be 9-18 digits')
      .optional(),
    ifscCode: z
      .string()
      .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC code format')
      .toUpperCase()
      .optional(),
    bankName: z.string().min(1).optional(),
    vpaAddress: z
      .string()
      .regex(/^[\w.-]+@[\w.-]+$/, 'Invalid UPI ID format')
      .optional(),
  })
  .refine(
    (data) => {
      if (data.type === PaymentMethodType.BANK_ACCOUNT) {
        return !!(data.accountNumber && data.ifscCode && data.bankName);
      }
      if (data.type === PaymentMethodType.VPA) {
        return !!data.vpaAddress;
      }
      return false;
    },
    {
      message:
        'Bank accounts must have accountNumber, ifscCode, and bankName. VPA must have vpaAddress.',
    }
  );

export type ReplacePaymentMethodInput = z.infer<typeof ReplacePaymentMethodSchema>;

// Transaction Schemas
export const CreateTransactionSchema = z
  .object({
    transactionType: z.nativeEnum(TransactionType),
    beneficiaryId: z.string().min(1).optional(), // Legacy - for backwards compatibility
    paymentMethodId: z.string().min(1).optional(), // New - preferred field
    amount: z.number().positive('Amount must be greater than 0'),
    mode: z.nativeEnum(PaymentMode),
    purpose: z.string().min(1, 'Purpose is required'),
    customerPhone: z.string().optional(),
    notes: z.any().optional(),
    distributorId: z.string().optional(), // For CASH_COLLECTION type
    // Location capture for server-side geofence enforcement (m04). The DMT route
    // gates on these when present; the mobile app always sends them.
    gpsLat: z.number().min(-90).max(90).optional(),
    gpsLng: z.number().min(-180).max(180).optional(),
    gpsAccuracy: z.number().nonnegative().optional(),
  })
  .refine((data) => data.beneficiaryId || data.paymentMethodId, {
    message: 'Either beneficiaryId or paymentMethodId must be provided',
  });

export type CreateTransactionInput = z.infer<typeof CreateTransactionSchema>;

// Credit Extension Request Schema
export const CreateCreditExtensionRequestSchema = z.object({
  requestedLimit: z.number().positive('Requested limit must be greater than 0'),
  reason: z.string().optional(),
});

export const ReviewCreditExtensionRequestSchema = z.object({
  status: z.enum(['APPROVED', 'REJECTED']),
  adminNotes: z.string().optional(),
});

export type CreateCreditExtensionRequestInput = z.infer<typeof CreateCreditExtensionRequestSchema>;
export type ReviewCreditExtensionRequestInput = z.infer<typeof ReviewCreditExtensionRequestSchema>;

// Transaction Limits Schemas
export const UpdateTransactionLimitsSchema = z.object({
  perTransactionAmount: z
    .number()
    .nonnegative('Per-transaction amount must be non-negative')
    .optional(),
  dailyTransactionCount: z
    .number()
    .int()
    .nonnegative('Daily count must be non-negative')
    .optional(),
  dailyTransactionAmount: z.number().nonnegative('Daily amount must be non-negative').optional(),
  weeklyTransactionCount: z
    .number()
    .int()
    .nonnegative('Weekly count must be non-negative')
    .optional(),
  weeklyTransactionAmount: z.number().nonnegative('Weekly amount must be non-negative').optional(),
  monthlyTransactionCount: z
    .number()
    .int()
    .nonnegative('Monthly count must be non-negative')
    .optional(),
  monthlyTransactionAmount: z
    .number()
    .nonnegative('Monthly amount must be non-negative')
    .optional(),
  commissionPercentage: z
    .number()
    .min(0, 'Commission percentage must be non-negative')
    .max(100, 'Commission percentage cannot exceed 100%')
    .optional(),
  status: z.nativeEnum(TransactionLimitStatus).optional(),
});

export type UpdateTransactionLimitsInput = z.infer<typeof UpdateTransactionLimitsSchema>;

// Distributor Ledger Types
export interface DistributorLedgerEntry {
  id: string;
  distributorId: string;
  recordedByAdminId: string;
  amount: number;
  type: 'DEPOSIT' | 'ADVANCE'; // Type of ledger entry
  notes?: string;
  recordedAt: string;
  createdAt: string;
  metadata?: any;
}

export interface DistributorCollectionStats {
  distributorId: string;
  distributorName: string;
  totalCollectedToday: number;
  totalCollectedAllTime: number;
  totalDepositedToAdmin: number;
  pendingDeposit: number;
  lastCollectionDate?: string;
  lastDepositDate?: string;
  status?: UserStatus; // Add status field
  invitedBy?: {
    uid: string;
    role: string;
    fullName: string;
  };
}

export interface CollectionHistoryEntry {
  collectionDate: string;
  amount: number;
  distributorId: string;
  distributorName: string;
  notes?: string;
}

// Distributor Ledger Schemas
export const CreateDistributorDepositSchema = z
  .object({
    distributorId: z.string().min(1, 'Distributor ID is required'),
    amount: z.number().min(0, 'Amount must be 0 or greater'),
    notes: z.string().optional(),
    recordedAt: z.string().datetime().optional(),
    advancePayment: z.number().min(0, 'Advance payment must be 0 or greater').optional(),
  })
  .refine(
    (data) => {
      // If advance payment is provided, allow amount to be 0 or any value
      if (data.advancePayment && data.advancePayment > 0) {
        return true;
      }
      // Otherwise, amount must be greater than 0
      return data.amount > 0;
    },
    {
      message: 'Amount must be greater than 0 unless advance payment is provided',
      path: ['amount'],
    }
  );

export type CreateDistributorDepositInput = z.infer<typeof CreateDistributorDepositSchema>;

// Dealer Ledger Types
export interface DealerLedgerEntry {
  id: string;
  dealerId: string;
  recordedByAdminId: string;
  amount: number;
  type: 'ADVANCE' | 'COLLECTION'; // Type of ledger entry
  notes?: string;
  recordedAt: string;
  createdAt: string;
  metadata?: any;
}

export interface DealerCollectionStats {
  dealerId: string;
  dealerName: string;
  phoneNumber?: string;
  totalCollectedToday: number;
  totalCollectedAllTime: number;
  totalAdvanceGiven: number;
  pendingCollection: number; // Used credit that needs to be collected
  lastCollectionDate?: string;
  lastAdvanceDate?: string;
  status?: UserStatus;
  invitedBy?: {
    uid: string;
    role: string;
    fullName: string;
  };
  distributor?: {
    uid: string;
    fullName: string;
  };
}

// Dealer Ledger Schemas
export const CreateDealerAdvanceSchema = z.object({
  dealerId: z.string().min(1, 'Dealer ID is required'),
  amount: z.number().positive('Amount must be greater than 0'),
  notes: z.string().optional(),
  recordedAt: z.string().datetime().optional(),
});

export type CreateDealerAdvanceInput = z.infer<typeof CreateDealerAdvanceSchema>;

// Unified Transaction Type
// Represents both regular transactions and distributor ledger entries
export type UnifiedTransactionSource = 'transaction' | 'distributor_ledger';

export interface UnifiedTransaction {
  // Discriminator
  source: UnifiedTransactionSource;

  // Common fields
  id: string;
  amount: number;
  currency: string;
  timestamp: string; // initiatedAt for transactions, recordedAt for distributor ledger

  // For regular transactions
  transaction?: Transaction;

  // For distributor ledger entries
  distributorLedger?: DistributorLedgerEntry & {
    distributorName?: string;
    adminName?: string;
  };
}

// ----------------------------------------------------------------------
// Admin backend gaps — enums + Zod schemas (m05/m06/m10/m11)
// ----------------------------------------------------------------------

// --- Settlement
export enum SettlementStatus {
  PENDING = 'PENDING',
  CLOSED = 'CLOSED',
}

export const CloseSettlementDaySchema = z.object({
  note: z.string().max(500).optional(),
});
export const ReopenSettlementDaySchema = z.object({
  reason: z.string().min(1).max(500),
});
export type CloseSettlementDayInput = z.infer<typeof CloseSettlementDaySchema>;
export type ReopenSettlementDayInput = z.infer<typeof ReopenSettlementDaySchema>;

// --- Reconciliation
export enum ReconciliationMatchKind {
  MANUAL = 'MANUAL',
  AUTO = 'AUTO',
  WRITE_OFF = 'WRITE_OFF',
}

export const ReconciliationMatchSchema = z.object({
  txnId: z.string().min(1),
  providerRef: z.string().min(1),
  amount: z.number().positive().optional(),
  note: z.string().max(500).optional(),
});
export const ReconciliationWriteOffSchema = z.object({
  txnId: z.string().min(1),
  amount: z.number().positive(),
  reason: z.string().min(1).max(500),
});
export type ReconciliationMatchInput = z.infer<typeof ReconciliationMatchSchema>;
export type ReconciliationWriteOffInput = z.infer<typeof ReconciliationWriteOffSchema>;

// --- Float / nostro
export enum FloatSource {
  PROBE = 'PROBE',
  TOP_UP = 'TOP_UP',
  MANUAL = 'MANUAL',
}

export const FloatTopUpSchema = z.object({
  provider: z.string().min(1).default('eko'),
  amount: z.number().positive(),
  note: z.string().max(500).optional(),
});
export type FloatTopUpInput = z.infer<typeof FloatTopUpSchema>;

// --- Dispute / chargeback
export enum DisputeKind {
  DISPUTE = 'DISPUTE',
  CHARGEBACK = 'CHARGEBACK',
}

export const ChargebackRecordSchema = z.object({
  txnId: z.string().min(1),
  bankReference: z.string().min(1),
  reasonCode: z.string().min(1),
  reasonText: z.string().max(1000).optional(),
  partialAmount: z.number().positive().optional(),
  attachmentKey: z.string().optional(),
});
export type ChargebackRecordInput = z.infer<typeof ChargebackRecordSchema>;

// --- Commission batches
export enum CommissionBatchStatus {
  OPEN = 'OPEN',
  APPROVED = 'APPROVED',
  EXECUTING = 'EXECUTING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export enum CommissionBatchItemStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  FAILED = 'FAILED',
  REVERSED = 'REVERSED',
}

export const BuildCommissionBatchSchema = z.object({
  cycleLabel: z.string().min(1).max(40),
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  note: z.string().max(500).optional(),
});
export type BuildCommissionBatchInput = z.infer<typeof BuildCommissionBatchSchema>;

// --- Commission tiers
export enum CommissionTierLevel {
  DEALER = 'DEALER',
  DISTRIBUTOR = 'DISTRIBUTOR',
}

export const CommissionTierSchema = z.object({
  minAmount: z.number().nonnegative(),
  maxAmount: z.number().positive().nullable().optional(),
  percentage: z.number().min(0).max(100),
});
export const ReplaceCommissionTiersSchema = z.object({
  level: z.nativeEnum(CommissionTierLevel),
  tiers: z.array(CommissionTierSchema).min(1),
  effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type CommissionTierInput = z.infer<typeof CommissionTierSchema>;
export type ReplaceCommissionTiersInput = z.infer<typeof ReplaceCommissionTiersSchema>;

// --- OTA releases
export enum OtaChannel {
  DEALER = 'dealer',
  DISTRIBUTOR = 'distributor',
  ADMIN = 'admin',
  ALL = 'all',
}

export enum OtaStatus {
  DRAFT = 'DRAFT',
  STAGED = 'STAGED',
  ROLLING = 'ROLLING',
  HALTED = 'HALTED',
  ROLLBACK = 'ROLLBACK',
}

export const CreateOtaReleaseSchema = z.object({
  channel: z.nativeEnum(OtaChannel),
  version: z.string().regex(/^\d+\.\d+\.\d+(?:-[a-zA-Z0-9.-]+)?$/),
  bundleKey: z.string().min(1),
  bundleSize: z.number().int().positive().optional(),
  notes: z.string().max(1000).optional(),
});
export const OtaUploadUrlSchema = z.object({
  filename: z.string().min(1).max(200),
  channel: z.nativeEnum(OtaChannel),
});
export const OtaRolloutSchema = z.object({
  pct: z.number().int().min(0).max(100),
});
export type CreateOtaReleaseInput = z.infer<typeof CreateOtaReleaseSchema>;
export type OtaUploadUrlInput = z.infer<typeof OtaUploadUrlSchema>;
export type OtaRolloutInput = z.infer<typeof OtaRolloutSchema>;

// --- Export jobs
export enum ExportJobKind {
  TXNS = 'TXNS',
  COMMISSIONS = 'COMMISSIONS',
  LEDGER = 'LEDGER',
  VARIANCES = 'VARIANCES',
  USERS = 'USERS',
  GRIEVANCES = 'GRIEVANCES',
  AUDIT = 'AUDIT',
}

export enum ExportJobStatus {
  QUEUED = 'QUEUED',
  RUNNING = 'RUNNING',
  DONE = 'DONE',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export const StartExportJobSchema = z.object({
  kind: z.nativeEnum(ExportJobKind),
  filter: z.record(z.string(), z.unknown()).optional(),
});
export type StartExportJobInput = z.infer<typeof StartExportJobSchema>;

// --- Report format
export enum ReportFormat {
  JSON = 'json',
  CSV = 'csv',
}

// Export all types
export type { z };

// Grievance module
export * from './grievance';
